// UserDialog.js
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';


const steps = [
  'Basic Information',
  'Organization Details',
  'Professional Information',
  'Memberships & Publications',
  'Financial Details',
  'Membership Information',
];

export default function SeniorCitizenUserDialog({ open, onClose, userId, fetchUsers }) {
  const [activeStep, setActiveStep] = useState(0);

  // State for user fields
// Initial state for userData
  const [userData, setUserData] = useState({
    category: '',
    name: '',
    fatherOrHusbandName: '',
    address: '',
    pinCode: '',
    dateOfBirth: '',
    age: 0,
    email: '',
    phoneNumber: '',
    gender: '',
    qualification: '',
    organization: {
      name: '',
      address: '',
      phone: '',
      mobile: '',
      email: '',
    },
    preferredMailingAddress: '',
    currentDesignation: '',
    experienceYears: 0,
    pastExperience: [],
    papersPublished: [],
    memberships: [],
    additionalInfo: '',
    introducedBy: {
      name: '',
      membershipCertificateNumber: '',
    },
    paymentdetails: '',
    MSMECertificate: '',
    linkedInId: '',
    panNumber: '',
    companyName: '',
    natureOfIndustry: '',
    numberOfEmployees: '',
    membershipID: '',
    howDidYouKnowAboutUs: '',
    membershipCategory: '',
    tenure: '',
  });

  useEffect(() => {
    if (userId) {
      // Fetch user details for editing
      const fetchUserDetails = async () => {
        try {
          const response = await axios.get(`https://form.cpccbe.org/api/seniorcitizen/seniorcitizen/${userId}`);
          const user = response.data;

          // Convert dateOfBirth to a valid format for the input
          setUserData({
            ...user,
            dateOfBirth: user.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : '', // Format to YYYY-MM-DD
            organization: user.organization || {
              name: '',
              address: '',
              phone: '',
              mobile: '',
              email: '',
            },
            introducedBy: user.introducedBy || {
              name: '',
              membershipCertificateNumber: '',
            },
            pastExperience: user.pastExperience || [],
            papersPublished: user.papersPublished || [],
            memberships: user.memberships || [],
          });
        } catch (error) {
          console.error("Error fetching user details:", error);
        }
      };
      fetchUserDetails();
    } else {
      // Reset fields for adding a new user
      setUserData({
        category: '',
        name: '',
        fatherOrHusbandName: '',
        address: '',
        pinCode: '',
        dateOfBirth: '',
        age: 0,
        email: '',
        phoneNumber: '',
        gender: '',
        qualification: '',
        organization: {
          name: '',
          address: '',
          phone: '',
          mobile: '',
          email: '',
        },
        preferredMailingAddress: '',
        currentDesignation: '',
        experienceYears: 0,
        pastExperience: [],
        papersPublished: [],
        memberships: [],
        additionalInfo: '',
        introducedBy: {
          name: '',
          membershipCertificateNumber: '',
        },
        paymentdetails: '',
        MSMECertificate: '',
        linkedInId: '',
        panNumber: '',
        companyName: '',
        natureOfIndustry: '',
        numberOfEmployees: '',
        membershipID: '',
        howDidYouKnowAboutUs: '',
        membershipCategory: '',
        tenure: '',
      });
    }
  }, [userId]);

  const handleUpdateUser = async () => {
    try {
      
      const formattedUserData = {
        ...userData,
        dateOfBirth: userData.dateOfBirth ? new Date(userData.dateOfBirth) : null,
        pastExperience: userData.pastExperience.map(exp => ({
          ...exp,
          from: exp.from ? new Date(exp.from) : null,
          to: exp.to ? new Date(exp.to) : null,
        })),
        papersPublished: userData.papersPublished.map(paper => ({
          ...paper,
          date: paper.date ? new Date(paper.date) : null,
        })),
      };

      // Create new user
      await axios.put(`https://form.cpccbe.org/api/seniorcitizen/${userId}`, formattedUserData);

      fetchUsers(); // Refresh user list after add/update
      onClose(); // Close dialog after adding/updating
    } catch (error) {
      console.error("Error saving user:", error);
    }
  };



  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  
  // Helper functions for array fields
  const handleAddPastExperience = () => {
    setUserData({
      ...userData,
      pastExperience: [
        ...userData.pastExperience,
        { from: '', to: '', designation: '', details: '' }
      ]
    });
  };

  const handleUpdatePastExperience = (index, field, value) => {
    const updatedExperiences = [...userData.pastExperience];
    updatedExperiences[index][field] = value;
    setUserData({
      ...userData,
      pastExperience: updatedExperiences
    });
  };

  const handleRemovePastExperience = (index) => {
    const updatedExperiences = [...userData.pastExperience];
    updatedExperiences.splice(index, 1);
    setUserData({
      ...userData,
      pastExperience: updatedExperiences
    });
  };

  const handleAddPaper = () => {
    setUserData({
      ...userData,
      papersPublished: [
        ...userData.papersPublished,
        { title: '', occasion: '', date: '', remarks: '' }
      ]
    });
  };

  const handleUpdatePaper = (index, field, value) => {
    const updatedPapers = [...userData.papersPublished];
    updatedPapers[index][field] = value;
    setUserData({
      ...userData,
      papersPublished: updatedPapers
    });
  };

  const handleRemovePaper = (index) => {
    const updatedPapers = [...userData.papersPublished];
    updatedPapers.splice(index, 1);
    setUserData({
      ...userData,
      papersPublished: updatedPapers
    });
  };

  const handleAddMembership = () => {
    setUserData({
      ...userData,
      memberships: [
        ...userData.memberships,
        { institution: '', category: '', professionalRemarks: '', socialServiceCategory: '', remarks: '' }
      ]
    });
  };

  const handleUpdateMembership = (index, field, value) => {
    const updatedMemberships = [...userData.memberships];
    updatedMemberships[index][field] = value;
    setUserData({
      ...userData,
      memberships: updatedMemberships
    });
  };

  const handleRemoveMembership = (index) => {
    const updatedMemberships = [...userData.memberships];
    updatedMemberships.splice(index, 1);
    setUserData({
      ...userData,
      memberships: updatedMemberships
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Senior Citizen User</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ my: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Basic Information */}
        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={userData.category || ''}
                  onChange={(e) => setUserData({ ...userData, category: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="" disabled>Select Category</MenuItem>
                  <MenuItem value="STUDENT">STUDENT</MenuItem>
                  <MenuItem value="SALARIED">SALARIED</MenuItem>
                  <MenuItem value="SELF EMPLOYED">SELF EMPLOYED</MenuItem>
                  <MenuItem value="RETIRED">RETIRED</MenuItem>
                  <MenuItem value="HOUSEWIFE">HOUSEWIFE</MenuItem>
                  <MenuItem value="UNEMPLOYED">UNEMPLOYED</MenuItem>
                  <MenuItem value="OTHERS">OTHERS</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Name"
                value={userData.name || ''}
                onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Father/Husband Name"
                value={userData.fatherOrHusbandName || ''}
                onChange={(e) => setUserData({ ...userData, fatherOrHusbandName: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={userData.address || ''}
                onChange={(e) => setUserData({ ...userData, address: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Pin Code"
                value={userData.pinCode || ''}
                onChange={(e) => setUserData({ ...userData, pinCode: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={userData.gender || ''}
                  onChange={(e) => setUserData({ ...userData, gender: e.target.value })}
                  fullWidth
                  required
                >
                  <MenuItem value="" disabled>Select Gender</MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={userData.email || ''}
                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone Number"
                value={userData.phoneNumber || ''}
                onChange={(e) => setUserData({ ...userData, phoneNumber: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Date of Birth"
                type="date"
                value={userData.dateOfBirth || ''}
                onChange={(e) => setUserData({ ...userData, dateOfBirth: e.target.value })}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Age"
                type="number"
                value={userData.age || ''}
                onChange={(e) => setUserData({ ...userData, age: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
          </Grid>
        )}

        {/* Organization Details */}
        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Organization Name"
                value={userData.organization?.name || ''}
                onChange={(e) =>
                  setUserData({
                    ...userData,
                    organization: { ...userData.organization, name: e.target.value },
                  })
                }
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Organization Address"
                value={userData.organization?.address || ''}
                onChange={(e) =>
                  setUserData({
                    ...userData,
                    organization: { ...userData.organization, address: e.target.value },
                  })
                }
                fullWidth
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Organization Phone"
                value={userData.organization?.phone || ''}
                onChange={(e) =>
                  setUserData({
                    ...userData,
                    organization: { ...userData.organization, phone: e.target.value },
                  })
                }
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Organization Mobile"
                value={userData.organization?.mobile || ''}
                onChange={(e) =>
                  setUserData({
                    ...userData,
                    organization: { ...userData.organization, mobile: e.target.value },
                  })
                }
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Organization Email"
                value={userData.organization?.email || ''}
                onChange={(e) =>
                  setUserData({
                    ...userData,
                    organization: { ...userData.organization, email: e.target.value },
                  })
                }
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Preferred Mailing Address"
                value={userData.preferredMailingAddress || ''}
                onChange={(e) => setUserData({ ...userData, preferredMailingAddress: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Company Name"
                value={userData.companyName || ''}
                onChange={(e) => setUserData({ ...userData, companyName: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nature of Industry"
                value={userData.natureOfIndustry || ''}
                onChange={(e) => setUserData({ ...userData, natureOfIndustry: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Number of Employees</InputLabel>
                <Select
                  label="Number of Employees"
                  value={userData.numberOfEmployees || ''}
                  onChange={(e) => setUserData({ ...userData, numberOfEmployees: e.target.value })}
                  fullWidth
                  required
                >
                  <MenuItem value="" disabled>Select Number of Employees</MenuItem>
                  <MenuItem value="0-50">0-50</MenuItem>
                  <MenuItem value="100-1000">100-1000</MenuItem>
                  <MenuItem value="1000+">1000+</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {/* Professional Information */}
        {activeStep === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Qualification"
                value={userData.qualification || ''}
                onChange={(e) => setUserData({ ...userData, qualification: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Current Designation"
                value={userData.currentDesignation || ''}
                onChange={(e) => setUserData({ ...userData, currentDesignation: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Experience (Years)"
                type="number"
                value={userData.experienceYears || ''}
                onChange={(e) => setUserData({ ...userData, experienceYears: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="LinkedIn Profile URL"
                value={userData.linkedInId || ''}
                onChange={(e) => setUserData({ ...userData, linkedInId: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="h6">Past Experience</Typography>
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={handleAddPastExperience}
                  variant="outlined"
                  size="small"
                >
                  Add Experience
                </Button>
              </Box>
              
              {userData.pastExperience.map((exp, index) => (
                <Box key={index} sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, mt: 2, position: 'relative' }}>
                  <IconButton 
                    size="small" 
                    sx={{ position: 'absolute', top: 5, right: 5 }}
                    onClick={() => handleRemovePastExperience(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="From Date"
                        type="date"
                        value={exp.from ? new Date(exp.from).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleUpdatePastExperience(index, 'from', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="To Date"
                        type="date"
                        value={exp.to ? new Date(exp.to).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleUpdatePastExperience(index, 'to', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Designation"
                        value={exp.designation || ''}
                        onChange={(e) => handleUpdatePastExperience(index, 'designation', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Details"
                        value={exp.details || ''}
                        onChange={(e) => handleUpdatePastExperience(index, 'details', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Additional Info"
                value={userData.additionalInfo || ''}
                onChange={(e) => setUserData({ ...userData, additionalInfo: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        )}

        {/* Memberships & Publications */}
        {activeStep === 3 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="h6">Papers Published</Typography>
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={handleAddPaper}
                  variant="outlined"
                  size="small"
                >
                  Add Paper
                </Button>
              </Box>
              
              {userData.papersPublished.map((paper, index) => (
                <Box key={index} sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, mt: 2, position: 'relative' }}>
                  <IconButton 
                    size="small" 
                    sx={{ position: 'absolute', top: 5, right: 5 }}
                    onClick={() => handleRemovePaper(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Title"
                        value={paper.title || ''}
                        onChange={(e) => handleUpdatePaper(index, 'title', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Occasion"
                        value={paper.occasion || ''}
                        onChange={(e) => handleUpdatePaper(index, 'occasion', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Date"
                        type="date"
                        value={paper.date ? new Date(paper.date).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleUpdatePaper(index, 'date', e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Remarks"
                        value={paper.remarks || ''}
                        onChange={(e) => handleUpdatePaper(index, 'remarks', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4 }}>
                <Typography variant="h6">Memberships</Typography>
                <Button 
                  startIcon={<AddIcon />} 
                  onClick={handleAddMembership}
                  variant="outlined"
                  size="small"
                >
                  Add Membership
                </Button>
              </Box>
              
              {userData.memberships.map((membership, index) => (
                <Box key={index} sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1, mt: 2, position: 'relative' }}>
                  <IconButton 
                    size="small" 
                    sx={{ position: 'absolute', top: 5, right: 5 }}
                    onClick={() => handleRemoveMembership(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Institution"
                        value={membership.institution || ''}
                        onChange={(e) => handleUpdateMembership(index, 'institution', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Category"
                        value={membership.category || ''}
                        onChange={(e) => handleUpdateMembership(index, 'category', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Professional Remarks"
                        value={membership.professionalRemarks || ''}
                        onChange={(e) => handleUpdateMembership(index, 'professionalRemarks', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Social Service Category"
                        value={membership.socialServiceCategory || ''}
                        onChange={(e) => handleUpdateMembership(index, 'socialServiceCategory', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Remarks"
                        value={membership.remarks || ''}
                        onChange={(e) => handleUpdateMembership(index, 'remarks', e.target.value)}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Introduced By</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Name"
                    value={userData.introducedBy?.name || ''}
                    onChange={(e) => setUserData({ 
                      ...userData, 
                      introducedBy: { 
                        ...userData.introducedBy, 
                        name: e.target.value 
                      } 
                    })}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Membership Certificate Number"
                    value={userData.introducedBy?.membershipCertificateNumber || ''}
                    onChange={(e) => setUserData({ 
                      ...userData, 
                      introducedBy: { 
                        ...userData.introducedBy, 
                        membershipCertificateNumber: e.target.value 
                      } 
                    })}
                    fullWidth
                    margin="normal"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Financial Details */}
        {activeStep === 4 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Payment Details</InputLabel>
                <Select
                  label="Payment Details"
                  value={userData.paymentdetails || ''}
                  onChange={(e) => setUserData({ ...userData, paymentdetails: e.target.value })}
                  fullWidth
                >
                  <MenuItem value="" disabled>Select Payment Details</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="MSME Certificate"
                value={userData.MSMECertificate || ''}
                onChange={(e) => setUserData({ ...userData, MSMECertificate: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="PAN Number"
                value={userData.panNumber || ''}
                onChange={(e) => setUserData({ ...userData, panNumber: e.target.value })}
                fullWidth
                margin="normal"
                required
              />
            </Grid>
          </Grid>
        )}

      
              {/* Membership Information */}
              {activeStep === 5 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Membership ID"
                      value={userData.membershipID || ''}
                      onChange={(e) => setUserData({ ...userData, membershipID: e.target.value })}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Membership Category"
                      value={userData.membershipCategory || ''}
                      onChange={(e) => setUserData({ ...userData, membershipCategory: e.target.value })}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Tenure"
                      value={userData.tenure || ''}
                      onChange={(e) => setUserData({ ...userData, tenure: e.target.value })}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>How did you know about us</InputLabel>
                      <Select 
                        label="How did you know about us"
                        value={userData.howDidYouKnowAboutUs || ''}
                        onChange={(e) => setUserData({ ...userData, howDidYouKnowAboutUs: e.target.value })}
                        fullWidth
                      >
                       <MenuItem value="" disabled>How did you know about us</MenuItem>
                                       <MenuItem value="Google">Google</MenuItem>
                                       <MenuItem value="Facebook">Facebook</MenuItem>
                                       <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                                       <MenuItem value="Twitter">Twitter</MenuItem>
                                       <MenuItem value="Instagram">Instagram</MenuItem>
                                       <MenuItem value="Referral">Referral</MenuItem>
                                       <MenuItem value="Others">Others</MenuItem>  
                      </Select> 
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              {activeStep > 0 && (
                <Button onClick={handleBack}>Back</Button>
              )}
      
              {activeStep === steps.length - 1 ? (
                <Button onClick={handleUpdateUser} variant="contained" color="primary">Update User</Button>
              ) : (
                <Button onClick={handleNext} variant="contained" color="primary">Next</Button>
              )}
      
              <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
          </Dialog>
        );
      }   